import { useCallback, useMemo } from "react";
import { Table, Group, Text } from "@mantine/core";
import {
  IconChevronUp,
  IconChevronDown,
  IconSelector,
} from "@tabler/icons-react";
import type {
  ObservationSortField,
  ObservationSortOrder,
} from "../../services/observations.service";

interface ObservationSortHeaderProps {
  field: ObservationSortField;
  label: string;
  sortBy: ObservationSortField;
  sortOrder: ObservationSortOrder;
  onSort: (field: ObservationSortField) => void;
  width?: number;
}

export function ObservationSortHeader({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
  width,
}: ObservationSortHeaderProps) {
  const isSorted = useMemo(() => sortBy === field, [sortBy, field]);

  const handleSortClick = useCallback(() => {
    onSort(field);
  }, [field, onSort]);

  const ariaSort = useMemo(() => {
    if (!isSorted) return "none" as const;
    return sortOrder === "asc"
      ? ("ascending" as const)
      : ("descending" as const);
  }, [isSorted, sortOrder]);

  return (
    <Table.Th
      style={{ cursor: "pointer", userSelect: "none", width }}
      onClick={handleSortClick}
      aria-sort={ariaSort}
    >
      <Group gap={4} wrap="nowrap">
        <Text size="sm" fw={700}>
          {label}
        </Text>
        {!isSorted && <IconSelector size={14} opacity={0.4} />}
        {isSorted && sortOrder === "asc" && <IconChevronUp size={14} />}
        {isSorted && sortOrder === "desc" && <IconChevronDown size={14} />}
      </Group>
    </Table.Th>
  );
}

export default ObservationSortHeader;
